/**
 * Bounded-context helper for the on-device chat.
 *
 * Bonsai 1.7B has an 8192-token window but performance degrades sharply past
 * ~3k tokens of prefix prefill. We keep the most recent `KEEP_TURNS` user/
 * assistant pairs verbatim and collapse anything older into a synthetic
 * user+assistant exchange that summarises what was discussed.
 *
 * `historyRef` in the page still holds the full transcript (for rendering);
 * this transform is applied only when posting to the worker.
 */

export interface Msg {
  role: 'user' | 'assistant';
  content: string;
}

export interface TruncateResult {
  /** History to send to the model (may be the original array if no truncation happened). */
  history: Msg[];
  /** True if the history was rewritten — caller should reset the worker's KV cache. */
  didTruncate: boolean;
}

/** ~6 turns = 12 messages. Tuned empirically for Bonsai 1.7B prefill latency. */
const KEEP_MESSAGES = 12;
const SUMMARY_MAX_USER_MSGS = 6;
const SUMMARY_SNIPPET_LEN = 80;

function stripTags(s: string): string {
  return s
    .replace(/<tool_result>[\s\S]*?<\/tool_result>/g, '')
    .replace(/<context>[\s\S]*?<\/context>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build a short textual digest of older user messages — strips any `<tool_result>`
 * payloads so we don't quote JSON back at the model, and trims long messages.
 */
function summariseOlder(older: Msg[]): string {
  const userBits = older
    .filter((m) => m.role === 'user')
    .map((m) => stripTags(m.content))
    .filter((s) => s.length > 0)
    .slice(-SUMMARY_MAX_USER_MSGS)
    .map((s) =>
      s.length > SUMMARY_SNIPPET_LEN ? `${s.slice(0, SUMMARY_SNIPPET_LEN - 1)}…` : s,
    );

  if (userBits.length === 0) {
    return 'Earlier in this conversation, the user explored skincare topics.';
  }
  return `Earlier in this conversation, the user discussed: ${userBits.join('; ')}.`;
}

/**
 * Return `history` unchanged if it's short enough; otherwise replace the older
 * prefix with a synthetic two-message summary so the kept tail still alternates
 * user/assistant cleanly.
 */
export function truncateHistory(history: Msg[]): TruncateResult {
  if (history.length <= KEEP_MESSAGES) {
    return { history, didTruncate: false };
  }

  // Slide the cut forward until we find a `user` message to start the kept tail
  // — the chat template expects user/assistant alternation after the system.
  let keptStart = history.length - KEEP_MESSAGES;
  while (keptStart < history.length && history[keptStart].role !== 'user') {
    keptStart += 1;
  }
  if (keptStart >= history.length) {
    // Degenerate case: tail is all-assistant. Just return original to be safe.
    return { history, didTruncate: false };
  }

  const older = history.slice(0, keptStart);
  const kept = history.slice(keptStart);
  if (older.length === 0) {
    return { history, didTruncate: false };
  }

  const summary = summariseOlder(older);
  const rewritten: Msg[] = [
    { role: 'user', content: '[Continuation of prior conversation]' },
    { role: 'assistant', content: summary },
    ...kept,
  ];
  return { history: rewritten, didTruncate: true };
}
